import React, { useState, useEffect, useContext, useRef } from "react";
import { useParams } from "react-router-dom";
import Picker from '@emoji-mart/react';
import data from '@emoji-mart/data';
import MicRecorder from "mic-recorder-to-mp3";
import clsx from "clsx";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import InputBase from "@material-ui/core/InputBase";
import CircularProgress from "@material-ui/core/CircularProgress";
import { green } from "@material-ui/core/colors";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import IconButton from "@material-ui/core/IconButton";
import MoodIcon from "@material-ui/icons/Mood";
import SendIcon from "@material-ui/icons/Send";
import CancelIcon from "@material-ui/icons/Cancel";
import ClearIcon from "@material-ui/icons/Clear";
import MicIcon from "@material-ui/icons/Mic";
import ScheduleIcon from '@material-ui/icons/Schedule';
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import HighlightOffIcon from "@material-ui/icons/HighlightOff";
import MoreVertIcon from '@material-ui/icons/MoreVert';
import VideoCallIcon from '@material-ui/icons/VideoCall';
import {
  FormControlLabel,
  Hidden,
  Menu,
  MenuItem,
  Switch,
  useTheme,
  Box,
  Grid,
  Chip
} from "@material-ui/core";
import ClickAwayListener from "@material-ui/core/ClickAwayListener";
import ScheduleMessageModal from '../ScheduleMessageModal';
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import RecordingTimer from "./RecordingTimer";
import { ReplyMessageContext } from "../../context/ReplyingMessage/ReplyingMessageContext";
import { AuthContext } from "../../context/Auth/AuthContext";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import toastError from "../../errors/toastError";
import Mustache from "mustache";
import NoteAddIcon from '@material-ui/icons/NoteAdd';
import NoteModal from '../NoteModal'; 
import ConfirmationModal from "../ConfirmationModal";
import MarkdownWrapper from "../MarkdownWrapper";
import { toast } from "react-toastify";
import socket from "../../services/socket-io";
import { hashDiagnosticId, recordDiagnosticEvent, useDiagnosticRender } from "../../services/frontendDiagnostics";

const Mp3Recorder = new MicRecorder({ bitRate: 128 });

const useStyles = makeStyles((theme) => ({
  mainWrapper: {
    backgroundColor:
      theme.palette.type === "dark" ? theme.palette.background.default : "#eee",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    borderTop: `1px solid ${theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.12)" : "rgba(0, 0, 0, 0.12)"
      }`,
    [theme.breakpoints.down("sm")]: {
      position: "fixed",
      bottom: 0,
      width: "100%",
    },
  },

  newMessageBox: {
    background:
      theme.palette.type === "dark" ? theme.palette.background.default : "#eee",
    width: "100%",
    display: "flex",
    padding: "7px",
    alignItems: "center",
  },

  messageInputWrapper: {
    backgroundColor:
      theme.palette.type === "dark" ? theme.palette.background.default : "#fff",
    border: `2px solid ${theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.12)" : "rgba(0, 0, 0, 0.12)"
      }`,
    padding: 6,
    marginRight: 7,
    display: "flex",
    borderRadius: 20,
    flex: 1,
    position: "relative",
  },

  messageInput: {
    paddingLeft: 10,
    flex: 1,
    border: "none",
  },

  sendMessageIcons: {
    color: theme.palette.type === "dark" ? "#e0e0e0" : "#555",
    transition: "color 0.2s ease",
    "&:hover": {
      color: theme.palette.type === "dark" ? "#fff" : "#000",
    },
  },

  uploadInput: {
    display: "none",
  },

  viewMediaInputWrapper: {
    display: "flex",
    padding: "10px 13px",
    position: "relative",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor:
      theme.palette.type === "dark" ? theme.palette.background.default : "#eee",
    borderTop: "1px solid rgba(0, 0, 0, 0.12)",
  },

  emojiBox: {
    position: "absolute",
    bottom: 63,
    width: 40,
    borderTop: "1px solid #e8e8e8",
  },

  circleLoading: {
    color: green[500],
    opacity: "70%",
    position: "absolute",
    top: "20%",
    left: "50%",
    marginLeft: -12,
  },

  audioLoading: {
    color: green[500],
    opacity: "70%",
  },

  recorderWrapper: {
    display: "flex",
    alignItems: "center",
    alignContent: "middle",
  },

  cancelAudioIcon: {
    color: "red",
  },

  sendAudioIcon: {
    color: "green",
  },

  replyginMsgWrapper: {
    display: "flex",
    width: "100%",
    alignItems: "center",
    justifyContent: "center",
    paddingTop: 8,
    paddingLeft: 73,
    paddingRight: 7,
  },

  replyginMsgContainer: {
    flex: 1,
    marginRight: 5,
    overflowY: "hidden",
    backgroundColor: "rgba(0, 0, 0, 0.05)",
    borderRadius: "7.5px",
    display: "flex",
    position: "relative",
  },

  replyginMsgBody: {
    padding: 10,
    height: "auto",
    display: "block",
    whiteSpace: "pre-wrap",
    overflow: "hidden",
  },

  replyginContactMsgSideColor: {
    flex: "none",
    width: "4px",
    backgroundColor: "#35cd96",
  },

  replyginSelfMsgSideColor: {
    flex: "none",
    width: "4px",
    backgroundColor: "#6bcbef",
  },

  messageContactName: {
    display: "flex",
    color: "#6bcbef",
    fontWeight: 500,
  },

  messageQuickAnswersWrapper: {
    margin: 0,
    position: "absolute",
    bottom: "50px",
    background: theme.palette.type === "dark" ? "#2a2a2a" : "#ffffff",
    padding: "2px",
    border: "1px solid",
    borderColor: theme.palette.type === "dark" ? "#444" : "#CCC",
    left: 0,
    width: "100%",
    "& li": {
      listStyle: "none",
      "& a": {
        display: "block",
        padding: "8px",
        textOverflow: "ellipsis",
        overflow: "hidden",
        maxHeight: "32px",
        color: theme.palette.type === "dark" ? "#ddd" : "#333",
        "&:hover": {
          background: theme.palette.type === "dark" ? "#3a3a3a" : "#F1F1F1",
          cursor: "pointer",
        },
      },
    },
  },
}));

const MessageInput = ({ ticketStatus }) => {
  const theme = useTheme()
  const isDark = theme.palette.type === 'dark'
  const classes = useStyles();
  const { ticketId } = useParams();
  const [medias, setMedias] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [showEmoji, setShowEmoji] = useState(false);
  const [loading, setLoading] = useState(false);
  const [recording, setRecording] = useState(false);
  const [quickAnswers, setQuickAnswer] = useState([]); 
  const [allQuickAnswers, setAllQuickAnswers] = useState([]);
  const [typeBar, setTypeBar] = useState(false);
  const inputRef = useRef();
  const inputMediaRef = useRef();
  const [anchorEl, setAnchorEl] = useState(null);
  const { setReplyingMessage, replyingMessage } =
    useContext(ReplyMessageContext);
  const { user } = useContext(AuthContext);

  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [signMessage, setSignMessage] = useLocalStorage("signOption", true);
  const [currentTarget, setCurrentTarget] = useState(null);
  const [noteModalOpen, setNoteModalOpen] = useState(false);
  
  const [anchorElMore, setAnchorElMore] = useState(null);
  const [confirmMeetModalOpen, setConfirmMeetModalOpen] = useState(false);
  useDiagnosticRender("MessageInput", () => ({
    ticketIdHash: hashDiagnosticId(ticketId),
    counts: {
      inputLength: inputMessage.length,
      medias: medias.length,
      quickAnswers: quickAnswers.length,
      loading: loading ? 1 : 0,
      recording: recording ? 1 : 0,
      ticketOpen: ticketStatus === "open" ? 1 : 0
    },
    metadata: {
      typeBar: typeBar ? "1" : "0"
    }
  }));

  useEffect(() => {
    async function fetchQuickAnswers() {
      try {
        const { data } = await api.get("/quickAnswers/");
        setAllQuickAnswers(data.quickAnswers);
      } catch (err) {
        toastError(err);
      }
    }
    fetchQuickAnswers();
  }, []);
  
  useEffect(() => {
    const fetchTicketInfo = async () => {
      if (!ticketId) return;
      try {
        const { data } = await api.get(`/tickets/${ticketId}`);
        setCurrentTarget(data);
      } catch (err) {
        console.error("Could not fetch ticket info", err);
      }
    };
    fetchTicketInfo();
  }, [ticketId]);
  
  useEffect(() => {
    const eventName = "contact";

    const handleContactUpdate = (data) => {
      if (data.action === "update" && currentTarget && data.contact.id === currentTarget.contact.id) {
        setCurrentTarget(prevState => ({
          ...prevState,
          contact: data.contact,
        }));
      }
    };

    socket.on(eventName, handleContactUpdate);

    return () => {
      socket.off(eventName, handleContactUpdate);
    };
  }, [currentTarget]);
  
  useEffect(() => {
    inputRef.current.focus();
  }, [replyingMessage]);

  useEffect(() => {
    inputRef.current.focus();
    return () => {
      setInputMessage("");
      setShowEmoji(false);
      setMedias([]);
      setReplyingMessage(null);
    };
  }, [ticketId, setReplyingMessage]);

  const handleChangeInput = (e) => {
    setInputMessage(e.target.value);
    handleLoadQuickAnswer(e.target.value); 
  };

  const handleQuickAnswersClick = (message) => {
    if (!currentTarget?.contact) {
      toast.error("Error: No se pudo encontrar la información del contacto.");
      setInputMessage(message);
      setTypeBar(false);
      return;
    }

    const contact = currentTarget.contact;
    let view = {
      nombreContacto: contact.name,
      numeroContacto: contact.number,
      emailContacto: contact.email || "",
    };

    if (contact.extraInfo) {
      contact.extraInfo.forEach((info) => (view[info.name] = info.value));
    }
    
    const originalEscape = Mustache.escape;
    Mustache.escape = text => text;
    const renderedMessage = Mustache.render(message, view);
    Mustache.escape = originalEscape;

    setInputMessage(renderedMessage);
    setTypeBar(false);
  };

  const handleAddEmoji = (e) => {
    let emoji = e.native;
    setInputMessage((prevState) => prevState + emoji);
  };

  const handleChangeMedias = (e) => {
    if (!e.target.files) {
      return;
    }

    const selectedMedias = Array.from(e.target.files);
    setMedias(selectedMedias);
  };

  useEffect(() => {
    if (medias.length > 0 && inputMediaRef.current) {
      const input = inputMediaRef.current;
      input.focus();
      
      const length = input.value.length;
      input.setSelectionRange(length, length);
    }
  }, [medias]);
  
  const handleInputPaste = (e) => {
    if (e.clipboardData.files[0]) {
      setMedias([e.clipboardData.files[0]]);
    }
  };

  const handleUploadMedia = async (e) => {
    const startedAt = performance.now();
    setLoading(true);
    e.preventDefault();

    const formData = new FormData();
    formData.append("fromMe", true);
    
    const bodyContent = signMessage
      ? "*" + (user ? user.name : "") + ":*\n" + inputMessage.trim()
      : inputMessage.trim();
      
    formData.append("body", bodyContent);

    if (replyingMessage?.id) {
      formData.append("quotedMsgId", replyingMessage.id);
    }

    medias.forEach((media) => {
      formData.append("medias", media);
    });

    try {
      await api.post("/messages/" + ticketId, formData);

      setMedias([]);
      setInputMessage("");
      setReplyingMessage(null);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }

    recordDiagnosticEvent({
      eventType: "message_send",
      component: "MessageInput",
      ticketIdHash: hashDiagnosticId(ticketId),
      durationMs: performance.now() - startedAt,
      counts: {
        medias: medias.length,
        bodyLength: inputMessage.trim().length
      },
      metadata: {
        mode: "media"
      }
    });
  };

  const handleSendMessage = async () => {
    if (inputMessage.trim() === "") return;
    const startedAt = performance.now();
    setLoading(true);

    const message = {
      read: 1,
      fromMe: true,
      mediaUrl: "",
      body: signMessage
        ? "*" + (user?.name || "") + ":*\n" + inputMessage.trim()
        : inputMessage.trim(),
      quotedMsgId: replyingMessage?.id,
    };

    try {
      await api.post("/messages/" + ticketId, message);

      setInputMessage("");
      setShowEmoji(false);
      setReplyingMessage(null);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }

    recordDiagnosticEvent({
      eventType: "message_send",
      component: "MessageInput",
      ticketIdHash: hashDiagnosticId(ticketId),
      durationMs: performance.now() - startedAt,
      counts: {
        bodyLength: message.body.length,
        quoted: replyingMessage ? 1 : 0
      },
      metadata: {
        mode: "text"
      }
    });
  };

  const handleStartRecording = async () => {
    setLoading(true);
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
      await Mp3Recorder.start();
      setRecording(true);
      setLoading(false);
    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  };

  const handleLoadQuickAnswer = (value) => {
    if (value && value.indexOf("/") === 0) {
      const searchValue = value.substring(1).toLowerCase();
      const filteredAnswers = allQuickAnswers.filter(
        (answer) => 
          answer.shortcut.toLowerCase().includes(searchValue) ||
          answer.message.toLowerCase().includes(searchValue)
      );
      setQuickAnswer(filteredAnswers);
      if (filteredAnswers.length > 0) {
        setTypeBar(true);
      } else {
        setTypeBar(false);
      }
    } else {
      setTypeBar(false);
    }
  };

  const handleUploadAudio = async () => {
    const startedAt = performance.now();
    setLoading(true);
    try {
      const [, blob] = await Mp3Recorder.stop().getMp3();
      
      if (blob.size < 10000) {
        setLoading(false);
        setRecording(false);
        return;
      }

      const formData = new FormData();
      const filename = `${new Date().getTime()}.mp3`;

      const audioBlob = new Blob([blob], { type: 'audio/mpeg' });

      formData.append("medias", audioBlob, filename);
      formData.append("body", "");

      if (replyingMessage?.id) {
        formData.append("quotedMsgId", replyingMessage.id);
      }

      await api.post(`/messages/${ticketId}`, formData);
      setReplyingMessage(null);
    } catch (err) {
      toastError(err);
    }

    setRecording(false);
    setLoading(false);
    recordDiagnosticEvent({
      eventType: "message_send",
      component: "MessageInput",
      ticketIdHash: hashDiagnosticId(ticketId),
      durationMs: performance.now() - startedAt,
      metadata: {
        mode: "audio"
      }
    });
  };

  const handleCancelAudio = async () => {
    try {
      await Mp3Recorder.stop().getMp3();
      setRecording(false);
    } catch (err) {
      toastError(err);
    }
  };

  const handleOpenMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuItemClick = (event) => {
    setAnchorEl(null);
  };

  const handleOpenMeetModal = (event) => {
    if (currentTarget.contact.email) {
      setConfirmMeetModalOpen(true);
    } else {
      toast.error(
        "El contacto no tiene email y se necesita uno para hacer la llamada. Haz click en la foto de perfil del contacto para editarlo.",
        { autoClose: 12000 }
      );
    }
  };

  const handleCreateMeet = async () => {
    toast.info('Creando videoconferencia...');
    try {
      const res = await api.post('/meet/create', {
        contactId: currentTarget?.contact.id,
        ticketId,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Mexico_City',
      });

      const data = res.data;

      if (data.needsAuth && data.authUrl) {
        toast.info('Redirigiendo a Google para vincular tu cuenta...');
        setTimeout(() => {
            window.location.href = data.authUrl;
        }, 1500);
        return;
      }

      if (data.meetLink) {
        window.open(data.meetLink, '_blank');
        
        if (data.messageError) {
            toast.warn(`Videollamada creada, pero: ${data.messageError}`, { autoClose: 10000 });
        } else {
            toast.success('Videollamada creada y enviada correctamente');
        }
        return;
      }

      toastError('No se pudo crear la videollamada.');
    } catch (err) {
      toastError(err);
    } finally {
      setConfirmMeetModalOpen(false);
    }
  };

  const renderReplyingMessage = (message) => {
    return (
      <div className={classes.replyginMsgWrapper}>
        <div className={classes.replyginMsgContainer}>
          <span
            className={clsx(classes.replyginContactMsgSideColor, {
              [classes.replyginSelfMsgSideColor]: !message.fromMe,
            })}
          ></span>
          <div className={classes.replyginMsgBody}>
            {!message.fromMe && (
              <span className={classes.messageContactName}>
                {message.contact?.name}
              </span>
            )}
            <MarkdownWrapper>{message.body}</MarkdownWrapper>
          </div>
        </div>
        <IconButton
          aria-label="showRecorder"
          component="span"
          disabled={loading || ticketStatus !== "open"}
          onClick={() => setReplyingMessage(null)}
        >
          <ClearIcon className={classes.sendMessageIcons} />
        </IconButton>
      </div>
    );
  };

  if (medias.length > 0)
    return (
      <>
        {replyingMessage && renderReplyingMessage(replyingMessage)}
        <Paper elevation={0} square className={classes.viewMediaInputWrapper}>
        <IconButton
          aria-label="cancel-upload"
          component="span"
          onClick={(e) => {
             setMedias([]);
             setInputMessage("");
          }}
        >
          <CancelIcon className={classes.sendMessageIcons} />
        </IconButton>

        {loading ? (
          <div style={{ flex: 1, display: "flex", justifyContent: "center" }}>
            <CircularProgress className={classes.circleLoading} />
          </div>
        ) : (
          <Box display="flex" flexDirection="column" style={{ flex: 1, margin: "0 10px" }}>
            <Box display="flex" flexWrap="wrap" gap={1} mb={1} style={{ maxHeight: "60px", overflowY: "auto" }}>
              {medias.map((media, index) => (
                <Chip 
                  key={index} 
                  label={media.name} 
                  size="small" 
                  onDelete={() => {
                    const newMedias = medias.filter((_, i) => i !== index);
                    setMedias(newMedias);
                  }}
                  color="primary"
                  variant="outlined"
                />
              ))}
            </Box>
            
            <Paper variant="outlined" style={{ padding: "2px 10px", borderRadius: 20, display: "flex", alignItems: "center" }}>
                <InputBase
                  inputRef={inputMediaRef}
                  placeholder="Escribe un mensaje para estos archivos..."
                  fullWidth
                  multiline
                  maxRows={3}
                  value={inputMessage}
                  onChange={handleChangeInput}
                  disabled={loading}
                  onKeyPress={(e) => {
                    if (loading || e.shiftKey) return;
                    else if (e.key === "Enter") {
                      e.preventDefault();
                      handleUploadMedia(e);
                    }
                  }}
                />
            </Paper>
          </Box>
        )}

        <Hidden xsDown>
          <FormControlLabel
              style={{ marginRight: 0, marginLeft: 8 }}
              control={
                <Switch
                  size="small"
                  checked={signMessage}
                  onChange={(e) => setSignMessage(e.target.checked)}
                  color="primary"
                />
              }
              label={i18n.t("messagesInput.signMessage")}
              labelPlacement="start" 
          />
        </Hidden>
        
        <IconButton
          aria-label="send-upload"
          component="span"
          onClick={handleUploadMedia}
          disabled={loading}
        >
          <SendIcon className={classes.sendMessageIcons} />
        </IconButton>
        </Paper>
      </>
    );
  else {
    return (
      <Paper square elevation={0} className={classes.mainWrapper}>
        {replyingMessage && renderReplyingMessage(replyingMessage)}
        <div className={classes.newMessageBox}>
          <Hidden only={["sm", "xs"]}>
            <IconButton
              aria-controls="more-options-menu"
              aria-haspopup="true"
              onClick={(e) => setAnchorElMore(e.currentTarget)}
              disabled={loading || recording || ticketStatus !== "open"}
            >
              <MoreVertIcon className={classes.sendMessageIcons} />
            </IconButton>

            <Menu
              id="more-options-menu"
              anchorEl={anchorElMore}
              keepMounted
              open={Boolean(anchorElMore)}
              onClose={() => setAnchorElMore(null)}
              anchorOrigin={{ vertical: "top", horizontal: "left" }}
              transformOrigin={{ vertical: "bottom", horizontal: "left" }}
            >
              {user?.isDoctor && (
                <MenuItem
                  onClick={() => {
                    setAnchorElMore(null);
                    handleOpenMeetModal();
                  }}
                >
                  <VideoCallIcon fontSize="small" style={{ marginRight: 8 }} />
                  Crear videollamada
                </MenuItem>
              )}

              <MenuItem
                onClick={() => {
                  setAnchorElMore(null);
                  setScheduleModalOpen(true);
                }}
              >
                <ScheduleIcon fontSize="small" style={{ marginRight: 8 }} />
                Mensaje programado
              </MenuItem>

              <MenuItem
                onClick={() => {
                  setAnchorElMore(null);
                  setNoteModalOpen(true);
                }}
              >
                <NoteAddIcon fontSize="small" style={{ marginRight: 8 }} />
                Agregar nota
              </MenuItem>
            </Menu>
            <IconButton
              aria-label="emojiPicker"
              component="span"
              disabled={loading || recording || ticketStatus !== "open"}
              onClick={(e) => setShowEmoji((prevState) => !prevState)}
            >
              <MoodIcon className={classes.sendMessageIcons} />
            </IconButton>
            {showEmoji ? (
              <div className={classes.emojiBox}>
                <ClickAwayListener onClickAway={(e) => setShowEmoji(false)}>
                  <Picker
                    data={data}
                    onEmojiSelect={handleAddEmoji}
                    theme={isDark ? 'dark' : 'light'}
                    locale="es"
                    perLine={16}
                    previewPosition="none"
                    skinTonePosition="none"
                  />
                </ClickAwayListener>
              </div>
            ) : null}
      
            <input
              multiple
              type="file"
              id="upload-button"
              disabled={loading || recording || ticketStatus !== "open"}
              className={classes.uploadInput}
              onChange={handleChangeMedias}
            />
            <label htmlFor="upload-button">
              <IconButton
                aria-label="upload"
                component="span"
                disabled={loading || recording || ticketStatus !== "open"}
              >
                <AttachFileIcon className={classes.sendMessageIcons} />
              </IconButton>
            </label>
            <FormControlLabel
              style={{ marginRight: 7, color: "gray" }}
              label={i18n.t("messagesInput.signMessage")}
              labelPlacement="start"
              control={
                <Switch
                  size="small"
                  checked={signMessage}
                  onChange={(e) => {
                    setSignMessage(e.target.checked);
                  }}
                  name="showAllTickets"
                  color="primary"
                />
              }
            />
          </Hidden>
          <Hidden only={["md", "lg", "xl"]}>
            <IconButton
              aria-controls="simple-menu"
              aria-haspopup="true"
              onClick={handleOpenMenuClick}
              disabled={loading || recording || ticketStatus !== "open"}
            >
              <MoreVertIcon className={classes.sendMessageIcons} />
            </IconButton>

            <Menu
              id="simple-menu"
              keepMounted
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuItemClick}
              anchorOrigin={{ vertical: "top", horizontal: "left" }}
              transformOrigin={{ vertical: "bottom", horizontal: "left" }}
            >
              {user?.isDoctor && (
                <MenuItem
                  onClick={() => {
                    setAnchorEl(null);
                    handleOpenMeetModal();
                  }}
                >
                  <VideoCallIcon className={classes.sendMessageIcons} />
                </MenuItem>
              )}

              <MenuItem
                onClick={() => {
                  setAnchorEl(null);
                  setScheduleModalOpen(true);
                }}
              >
                <ScheduleIcon className={classes.sendMessageIcons} />
              </MenuItem>

              <MenuItem
                onClick={() => {
                  setAnchorEl(null);
                  setNoteModalOpen(true);
                }}
              >
                <NoteAddIcon className={classes.sendMessageIcons} />
              </MenuItem>

              <MenuItem
                onClick={() => {
                  setAnchorEl(null);
                  setShowEmoji((prev) => !prev);
                }}
              >
                <MoodIcon className={classes.sendMessageIcons} />
              </MenuItem>

              <MenuItem>
                <input
                  multiple
                  type="file"
                  id="upload-button-mobile"
                  disabled={loading || recording || ticketStatus !== "open"}
                  className={classes.uploadInput}
                  onChange={handleChangeMedias}
                />
                <label htmlFor="upload-button-mobile">
                  <AttachFileIcon className={classes.sendMessageIcons} />
                </label>
              </MenuItem>

              <MenuItem>
                <FormControlLabel
                  style={{ marginRight: 7 }}
                  labelPlacement="start"
                  control={
                    <Switch
                      size="small"
                      checked={signMessage}
                      onChange={(e) => setSignMessage(e.target.checked)}
                      name="showAllTickets"
                      color="primary"
                    />
                  }
                />
              </MenuItem>
            </Menu>
          </Hidden>
          <div className={classes.messageInputWrapper}>
            <InputBase
              inputRef={(input) => {
                input && input.focus();
                input && (inputRef.current = input);
              }}
              className={classes.messageInput}
              placeholder={
                ticketStatus === "open"
                  ? i18n.t("messagesInput.placeholderOpen")
                  : i18n.t("messagesInput.placeholderClosed")
              }
              multiline
              maxRows={5}
              value={inputMessage}
              onChange={handleChangeInput}
              disabled={recording || loading || ticketStatus !== "open"}
              onPaste={(e) => {
                ticketStatus === "open" && handleInputPaste(e);
              }}
              onKeyPress={(e) => {
                if (loading || e.shiftKey) return;
                else if (e.key === "Enter") {
                  handleSendMessage();
                }
              }}
            />
            {typeBar ? (
              <ul className={classes.messageQuickAnswersWrapper}>
                {quickAnswers.map((value, index) => {
                  return (
                    <li
                      className={classes.messageQuickAnswersWrapperItem}
                      key={index}
                    >
                      {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                      <a onClick={() => handleQuickAnswersClick(value.message)}>
                        {value.shortcut + " - " + value.message}
                      </a>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <div></div>
            )}
          </div>
          {inputMessage ? (
            <IconButton
              aria-label="sendMessage"
              component="span"
              onClick={handleSendMessage}
              disabled={loading}
            >
              <SendIcon className={classes.sendMessageIcons} />
            </IconButton>
          ) : recording ? (
            <div className={classes.recorderWrapper}>
              <IconButton
                aria-label="cancelRecording"
                component="span"
                fontSize="large"
                disabled={loading}
                onClick={handleCancelAudio}
              >
                <HighlightOffIcon className={classes.cancelAudioIcon} />
              </IconButton>
              {loading ? (
                <div>
                  <CircularProgress className={classes.audioLoading} />
                </div>
              ) : (
                <RecordingTimer />
              )}

              <IconButton
                aria-label="sendRecordedAudio"
                component="span"
                onClick={handleUploadAudio}
                disabled={loading}
              >
                <CheckCircleOutlineIcon className={classes.sendAudioIcon} />
              </IconButton>
            </div>
          ) : (
            (user?.canSendAudios !== false) && (
              <IconButton
                aria-label="showRecorder"
                component="span"
                disabled={loading || ticketStatus !== "open"}
                onClick={handleStartRecording}
              >
                <MicIcon className={classes.sendMessageIcons} />
              </IconButton>
            )
          )}
        </div>
        <ConfirmationModal
          open={confirmMeetModalOpen}
          onClose={() => setConfirmMeetModalOpen(false)}
          onConfirm={handleCreateMeet}
          title="Crear videoconferencia"
        >
          <p>
            Al confirmar, se creará una videollamada de Google Meet para este ticket.
          </p>
          <p>
            Si no tenés tu cuenta de Google vinculada, se te pedirá iniciar sesión.
          </p>
        </ConfirmationModal>

        <NoteModal
          open={noteModalOpen}
          onClose={() => setNoteModalOpen(false)}
          ticketId={ticketId}
        />
        <ScheduleMessageModal
          open={scheduleModalOpen}
          onClose={() => setScheduleModalOpen(false)}
          ticketId={ticketId}
        />
      </Paper>
    );
  }
};

export default MessageInput;
